import React from "react";
import { Button } from "./button";
import { Card } from "./card";
import { LucideIcon } from "lucide-react";

interface EmptyStateProps {
  icon: LucideIcon | React.ComponentType;
  title: string;
  description: string;
  actionLabel?: string;
  onAction?: () => void;
  secondaryActionLabel?: string;
  onSecondaryAction?: () => void;
  className?: string;
}

export function EmptyState({
  icon: Icon,
  title,
  description,
  actionLabel,
  onAction,
  secondaryActionLabel,
  onSecondaryAction,
  className = "",
}: EmptyStateProps) {
  return (
    <div
      className={`flex flex-col items-center justify-center py-16 ${className}`}
    >
      <div className="flex flex-col items-center text-center max-w-md">
        <div className="flex items-center justify-center w-16 h-16 rounded-full bg-muted/20 mb-6">
          <Icon className="h-8 w-8 text-muted-foreground" />
        </div>

        <h3 className="text-xl font-semibold text-foreground mb-2">{title}</h3>

        <p className="text-muted-foreground text-center mb-6 leading-relaxed">
          {description}
        </p>

        <div className="flex flex-col sm:flex-row gap-3">
          {actionLabel && onAction && (
            <Button onClick={onAction} className="min-w-[140px]">
              {actionLabel}
            </Button>
          )}

          {secondaryActionLabel && onSecondaryAction && (
            <Button
              variant="outline"
              onClick={onSecondaryAction}
              className="min-w-[140px]"
            >
              {secondaryActionLabel}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// Specific empty state variants
export function NoStudiesEmptyState({
  onCreateStudy,
}: {
  onCreateStudy: () => void;
}) {
  return (
    <Card className="border-dashed border-2 border-muted-foreground/25">
      <EmptyState
        icon={() => (
          <svg
            className="h-8 w-8"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
            />
          </svg>
        )}
        title="Welcome to Lorentzian Optimizer"
        description="Get started by creating your first trading strategy optimization study. Upload historical data, configure parameters, and let our AI find the best settings for profitable trading."
        actionLabel="Create Your First Study"
        onAction={onCreateStudy}
        secondaryActionLabel="Learn How It Works"
        onSecondaryAction={() =>
          window.open(
            "https://github.com/jbr1st/lorentzian-optimizer-microservice#readme",
            "_blank",
          )
        }
      />
    </Card>
  );
}

export function NoFilteredStudiesEmptyState({
  onClearFilters,
  searchQuery,
}: {
  onClearFilters: () => void;
  searchQuery?: string;
}) {
  const description = searchQuery
    ? `No studies found matching "${searchQuery}". Try adjusting your search or filters to see more results.`
    : "Try adjusting your filters to see more results, or clear all filters to view all your studies.";

  return (
    <EmptyState
      icon={() => (
        <svg
          className="h-8 w-8"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.414A1 1 0 013 6.707V4z"
          />
        </svg>
      )}
      title="No Studies Match Your Filters"
      description={description}
      actionLabel="Clear All Filters"
      onAction={onClearFilters}
    />
  );
}
